import type { ReactNode } from 'react';
import { Info, type LucideIcon } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BELOW_DESKTOP_QUERY } from '@/config/ui.constants';
import { cn } from '@/lib/utils';

interface InfoTipProps {
  /** The explanatory content shown in the tooltip/popover. */
  children: ReactNode;
  /** Accessible name for the trigger button, e.g. "About this service". */
  label: string;
  /** Icon glyph; defaults to the Info circle. */
  icon?: LucideIcon;
  /** Extra classes for the trigger button (icon color, alignment offsets). */
  triggerClassName?: string;
  /** Extra classes for the icon glyph (defaults to a 16px glyph). */
  iconClassName?: string;
  /** Extra classes for the floating content (e.g. max-width overrides). */
  contentClassName?: string;
  /** Placement of the floating content relative to the trigger. */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Supplementary-info icon trigger: a hover/focus tooltip on desktop, a
 * tap-to-open popover below 1024px (tooltips never open on touch), with a
 * 44px touch hit area around the 16px glyph.
 *
 * @category Feedback
 */
function InfoTip({
  children,
  label,
  icon: Icon = Info,
  triggerClassName,
  iconClassName,
  contentClassName,
  side = 'bottom',
}: InfoTipProps) {
  const isBelowLg = useMediaQuery(BELOW_DESKTOP_QUERY);

  const trigger = (
    <button
      type="button"
      aria-label={label}
      className={cn(
        // Desktop keeps the established bare 16px glyph; below lg the hit
        // area grows to the 44px touch floor without resizing the icon.
        'inline-flex shrink-0 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-lg:h-11 max-lg:w-11',
        triggerClassName,
      )}
    >
      <Icon className={cn('h-4 w-4', iconClassName)} aria-hidden="true" />
    </button>
  );

  if (isBelowLg) {
    return (
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          side={side}
          className={cn('max-w-xs p-2.5 text-sm', contentClassName)}
        >
          {children}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side={side} className={cn('max-w-xs', contentClassName)}>
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export { InfoTip };
